import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Section = styled.section`
  padding: 80px 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  color: #1f2937;

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const HighlightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const HighlightCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid #e5e7eb;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0,0,0,0.12);
  }
`;

const CardIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #1e40af, #3b82f6);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;

  i {
    font-size: 1.5rem;
    color: white;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #1f2937;
`;

const CardDescription = styled.p`
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const HighlightMetric = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MetricValue = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #059669;
`;

const MetricLabel = styled.span`
  color: #6b7280;
  font-size: 0.875rem;
`;

const HighlightQuote = styled.div`
  background: #f3f4f6;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #1e40af;

  p {
    font-style: italic;
    margin-bottom: 0.5rem;
    color: #374151;
  }

  cite {
    font-size: 0.875rem;
    color: #6b7280;
  }
`;

const Highlights = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const highlights = [
    {
      icon: 'fas fa-chart-line',
      title: 'Strong Community Turnout',
      description: 'Tech Week 2025 had 120+ events and 15,000+ attendees. The Right Place reported 16,000+ attendees in 2024 across 70+ events.',
      metric: { value: '120+', label: 'Events (2025)' }
    },
    {
      icon: 'fas fa-star',
      title: 'High-Profile Keynote',
      description: 'Apple co-founder Steve Wozniak headlined the kickoff event, drawing a sold-out crowd of 1,300 at GLC Live @ 20 Monroe.',
      quote: {
        text: '"His story happens when creativity meets technical brilliance"',
        author: '- Andria Romkema, The Right Place'
      }
    },
    {
      icon: 'fas fa-users',
      title: 'Diversity Committee Formation',
      description: 'New diversity-focused committee proposed to partner with Urban League, NAACP, Hispanic Chamber, Asian organizations, and community hubs.',
      metric: { value: '6+', label: 'Community Partners' }
    },
    {
      icon: 'fas fa-clipboard-check',
      title: 'Registration Innovation',
      description: 'Committee committed to outsourcing registration with on-demand name tag printing to eliminate check-in bottlenecks and streamline the process.',
      quote: {
        text: '"We need to fix the check-in process for next year"',
        author: '- Tiffany Falcon-Ewigleben'
      }
    },
    {
      icon: 'fas fa-calendar-alt',
      title: 'Year-Round Engagement',
      description: 'Proposed February/March information event and year-round partnership matchmaking through website contact system to recruit hosts and speakers.',
      metric: { value: '6 months', label: 'Early Pipeline' }
    },
    {
      icon: 'fas fa-map-marker-alt',
      title: 'Geographic Expansion',
      description: 'Muskegon Innovation Hub ready to participate next year, with discussions about expanding to Holland and other West Michigan communities.',
      quote: {
        text: '"We\'re ready to be part of Tech Week next year"',
        author: '- Megan, Muskegon Innovation Hub'
      }
    }
  ];

  return (
    <Section id="highlights">
      <Container>
        <SectionTitle
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          2025 Success Highlights
        </SectionTitle>
        
        <HighlightsGrid>
          {highlights.map((highlight, index) => (
            <HighlightCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <CardIcon>
                <i className={highlight.icon}></i>
              </CardIcon>
              
              <CardTitle>{highlight.title}</CardTitle>
              <CardDescription>{highlight.description}</CardDescription>
              
              {highlight.metric && (
                <HighlightMetric>
                  <MetricValue>{highlight.metric.value}</MetricValue>
                  <MetricLabel>{highlight.metric.label}</MetricLabel>
                </HighlightMetric>
              )}
              
              {highlight.quote && (
                <HighlightQuote>
                  <p>{highlight.quote.text}</p>
                  <cite>{highlight.quote.author}</cite>
                </HighlightQuote>
              )}
            </HighlightCard>
          ))}
        </HighlightsGrid>
      </Container>
    </Section>
  );
};

export default Highlights;
